// Knowledge base for the site assistant.
//
// Service entries are DERIVED from SERVICES in seoPages.js rather than copied,
// so adding a service page automatically teaches the assistant about it and the
// two can never drift apart. FAQs come from FAQS in site.js the same way.
//
// Everything below is answered locally — no API key, no per-message cost.
// Hand-written entries here are for things the page data doesn't carry:
// pricing policy, licensing, service area, small talk, and escalation.

import { NAP, FAQS } from "@/data/site";
import { SERVICES, CITIES } from "@/data/seoPages";

const CORE_CITIES = CITIES.filter((c) => c.core).map((c) => c.name);

// Extra phrasings visitors use that the SEO keywords don't cover. Keyed by
// service slug; merged into that service's match list.
const EXTRA_MATCH = {
  "custom-homes": ["build a house", "dream home", "spec home", "acreage"],
  "home-renovations": ["renovate my house", "gut job", "whole home", "fix up my house"],
  "kitchen-renovations": ["new kitchen", "cabinets", "countertops", "island"],
  "bathroom-renovations": ["shower", "bathtub", "powder room", "vanity"],
  // "basement suite" deliberately belongs to secondary-suites below, not here —
  // someone asking about a suite wants the legal-suite page, not basement finishing.
  "basement-development": ["finish my basement", "develop my basement", "basement reno"],
  "secondary-suites": ["legal suite", "basement suite", "garage suite", "laneway",
    "rental suite", "basement apartment", "suite permit", "mortgage helper"],
  roofing: ["roof leak", "leaking roof", "shingle", "reroof", "hail damage"],
  electrical: ["panel upgrade", "breaker", "outlet", "pot lights", "sub panel"],
  plumbing: ["water line", "drain", "hot water tank", "repipe", "leaky pipe"],
  hvac: ["furnace", "air conditioning", "heat pump", "ductwork", "no heat"],
  "concrete-foundations": ["cracked foundation", "footings"],
  "concrete-flatwork": ["driveway", "sidewalk", "garage pad", "slab", "patio"],
  "concrete-cutting": ["core drilling", "cut concrete", "saw cutting"],
  framing: ["load bearing", "remove a wall", "open concept", "beam", "stud wall"],
  "structural-renovations": ["load bearing wall", "take out a wall", "underpinning",
    "structural engineer"],
  demolition: ["tear down", "strip out", "gut", "knock down"],
  flooring: ["hardwood", "laminate", "vinyl plank", "lvp", "carpet", "subfloor"],
  tiling: ["backsplash", "tile floor", "shower tile", "porcelain"],
  waterproofing: ["leaking shower", "moisture barrier", "damp basement"],
  "kerdi-shower-waterproofing": ["kerdi", "schluter", "shower pan"],
  painting: ["repaint", "interior paint", "exterior paint"],
  "decks-fences": ["deck", "pergola", "patio cover", "gazebo", "fence"],
  "epoxy-flooring": ["garage floor coating", "shop floor", "seamless floor"],
  "home-additions": ["add a room", "second storey", "second story", "bump out", "expand my home"],
  "gas-fitting": ["gas line", "bbq line", "fireplace line", "gas fitter"],
  "disaster-restoration": ["flood", "water damage", "fire damage", "emergency", "restoration"],
  "tenant-improvements": ["build out", "lease space", "commercial space"],
  "web-design": ["website", "new site", "web developer", "landing page", "ecommerce"],
  branding: ["logo", "brand identity", "rebrand", "brand guidelines"],
  seo: ["google ranking", "search ranking", "rank on google", "local search"],
  "online-marketing": ["ads", "google ads", "advertising", "lead generation", "campaign"],
  "ai-automation": ["chatbot", "automation", "ai assistant", "workflow", "automate"],
  "social-media": ["instagram", "facebook", "social posts", "content"],
};

// Turn a slug into searchable words: "concrete-foundations" → "concrete foundations".
const slugWords = (slug) => slug.replace(/-/g, " ");

const serviceEntries = SERVICES.map((s) => ({
  id: s.slug,
  name: s.name,
  group: s.group,
  url: `/services/${s.slug}`,
  match: [
    s.name,
    s.keyword,
    slugWords(s.slug),
    ...(s.subKeywords || []),
    ...(EXTRA_MATCH[s.slug] || []),
  ],
  answer: [s.intro, "", ...(s.points || []).map((p) => `• ${p}`)].join("\n"),
}));

export const CHAT_KB = {
  business: {
    name: NAP.name,
    legalName: NAP.legalName,
    phone: NAP.phoneDisplay,
    phoneHref: NAP.phoneHref,
    email: NAP.email,
    address: `${NAP.street}, ${NAP.city} ${NAP.region} ${NAP.postalCode}`,
    hours: "Monday to Friday, 8:00am – 5:00pm",
    coreArea: CORE_CITIES.join(", "),
  },

  // Michael's steer (call 18 Aug 2026): small repair jobs cost more time than
  // they earn. The assistant turns them away politely instead of booking them.
  // This targets one-off handyman repairs specifically — not small-scope
  // projects, which the estimator still quotes.
  policy: {
    declineBelow: {
      enabled: true,
      id: "small-jobs",
      match: [
        "small repair", "tiny job", "small job", "quick fix", "handyman",
        "patch a hole", "one outlet", "change a light bulb", "fix a door",
        "minor repair", "odd job", "hang a shelf", "touch up paint",
        "fix a leak", "single repair", "just one small",
      ],
      answer:
        "Straight answer: MRE takes on **projects, not one-off repairs**. Renovations, additions, suites, new builds and commercial work are where we do our best work — a single small repair isn't something we can do at a price that's fair to you.\n\nIf the job is bigger than it first sounds, tell me more and I'll get it to the team.",
    },
  },

  services: serviceEntries,

  faqs: [
    // Pulled from the site's own FAQ section, with match terms added.
    ...FAQS.map((f, i) => ({
      id: `site-faq-${i}`,
      match: [f.q],
      answer: f.a,
    })),

    {
      id: "pricing",
      match: ["how much", "price", "pricing", "cost", "what do you charge",
        "rates", "ballpark", "budget", "expensive", "per square foot", "affordable"],
      answer:
        "Pricing depends on scope, materials and site conditions, so we don't publish flat rates — an honest number needs eyes on the project.\n\nTwo things we can tell you: the consultation and quote are **free**, and the quote you get is the number we work to. There's also an **instant estimator** on this site if you want a ballpark range right now.",
      cta: "estimate",
    },
    {
      id: "quote-cost",
      match: ["is the quote free", "free quote", "free estimate", "charge for a quote",
        "consultation cost", "does it cost to get a quote"],
      answer:
        "Free — consultations and quotes cost nothing. Tell me about your project and I'll pass it to the team, or use the instant estimator for a ballpark range.",
      cta: "estimate",
    },
    {
      id: "area",
      match: ["service area", "do you serve", "where are you", "location", "address",
        "airdrie", "cochrane", "okotoks", "chestermere", "edmonton", "red deer",
        "outside calgary", "do you travel", "areas covered"],
      answer:
        `We're based in Calgary at ${NAP.street}.\n\n**Construction** covers ${CORE_CITIES.slice(0, 5).join(", ")} and the wider Calgary region.\n\n**Digital work** — branding, web, marketing and AI — we do **Canada-wide**, since none of it needs a crew on site.`,
    },
    {
      id: "contact",
      match: ["contact", "phone number", "call you", "email", "talk to someone",
        "speak to a human", "real person", "reach you", "get in touch"],
      answer:
        `Here's how to reach us:\n\n📞 **${NAP.phoneDisplay}**\n✉️ **${NAP.email}**\n\nOr leave your details with me and someone will come back to you.`,
      cta: "lead",
    },
    {
      id: "hours",
      match: ["hours", "open", "when are you open", "weekend", "saturday", "sunday", "after hours"],
      answer:
        "Office hours are **Monday to Friday, 8:00am – 5:00pm**. Crews are often on site earlier. Anything you leave with me outside those hours is picked up the next business day.",
    },
    {
      id: "warranty",
      match: ["warranty", "guarantee", "if something goes wrong", "callback", "deficiency", "after the job"],
      answer:
        "We stand behind our workmanship, and manufacturer warranties on materials pass through to you. The exact coverage is written into your contract — ask at the consultation and you'll get it in writing, not verbally.",
    },
    {
      id: "financing",
      match: ["financing", "finance", "payment plan", "monthly payments", "loan", "pay over time", "deposit"],
      answer:
        "We don't offer financing directly, but we can point you toward the options people commonly use for renovations and builds in Alberta. Worth raising at your consultation.",
    },
    {
      id: "references",
      match: ["references", "reviews", "testimonials", "past work", "past projects",
        "portfolio", "examples", "photos", "gallery", "previous projects", "your work"],
      answer:
        "There's a [showroom](/showroom) of real MRE projects on this site, and the [work page](/work) has case studies across construction and digital.",
    },
    {
      id: "process",
      match: ["how does it work", "process", "what happens next", "first step", "get started", "steps"],
      answer:
        "Four steps:\n\n1. **Free consultation** — we listen first\n2. **Plan & quote** — clear scope, timeline and a transparent quote\n3. **Build & launch** — weekly updates\n4. **Handover & grow**\n\nStep one costs nothing. Want me to start it?",
      cta: "lead",
    },
    {
      id: "both-sides",
      match: ["construction and marketing", "both", "why both", "two businesses",
        "digital and construction", "what makes you different"],
      answer:
        "Most firms do one. MRE does both: we build the physical space your business lives in, **and** the brand, website and marketing that make it grow.\n\nIf you only need one side, that's fine too — plenty of clients come to us for just a build or just a website.",
    },
    {
      id: "emergency",
      match: ["emergency", "urgent", "right now", "asap", "flooding", "burst pipe", "no heat right now"],
      answer:
        `If it's an active emergency — water, gas or structural — call **${NAP.phoneDisplay}** now rather than waiting on this chat.`,
      escalate: true,
    },
  ],

  intents: [
    {
      id: "greeting",
      match: ["hi", "hello", "hey", "good morning", "good afternoon", "good evening", "yo", "howdy"],
      answer:
        "Hi — I'm the MRE assistant. I can tell you about our construction and digital services, how we work, and what we cover — or get your project to the team.\n\nWhat are you working on?",
    },
    {
      id: "thanks",
      match: ["thanks", "thank you", "appreciate it", "cheers", "ty"],
      answer: "Anytime. Anything else you want to know?",
    },
    {
      id: "bye",
      match: ["bye", "goodbye", "see you", "that is all", "nothing else", "im done", "i'm done"],
      answer: `Take care. When you want that free consultation, we're at **${NAP.phoneDisplay}**.`,
    },
    {
      id: "services-list",
      match: ["what do you do", "services", "what services", "list of services",
        "what can you build", "what do you offer", "help me with"],
      answer:
        "Two sides to MRE:\n\n**Build** — custom homes, renovations, additions, secondary suites, commercial build-outs, and the trades behind them: concrete, framing, electrical, plumbing, HVAC, roofing, flooring and more.\n\n**Grow** — branding, web design, SEO, online marketing, and AI & automation.\n\nWhich one are you asking about?",
    },
    {
      id: "human",
      match: ["are you a bot", "are you human", "are you ai", "is this a robot",
        "am i talking to a person", "are you real"],
      answer:
        "I'm an assistant on the MRE website — a program, not a person. I answer from MRE's own service information, and I'll hand you to a human whenever you want.\n\nWant me to do that now?",
      cta: "lead",
    },
    {
      id: "complaint",
      match: ["complaint", "unhappy", "problem with my job", "not finished", "poor work",
        "refund", "dispute", "angry", "lawyer"],
      answer:
        `That needs a person, not me. Call **${NAP.phoneDisplay}** or email **${NAP.email}** and it'll be looked at directly.\n\nIf you'd rather leave the details here, I'll flag it as urgent.`,
      escalate: true,
    },
    {
      id: "job-application",
      match: ["job", "hiring", "apply", "work for you", "resume", "subcontractor",
        "sub trade", "looking for work", "careers", "employment"],
      answer:
        `For work or subtrade enquiries, email **${NAP.email}** with your trade and certifications. That reaches the right person faster than this chat.`,
    },
    {
      id: "sales-pitch",
      match: ["i can get you leads", "buy backlinks", "crypto", "investment opportunity",
        "increase your ranking guaranteed"],
      answer: "Not the right channel for that — this chat is for MRE customers and project enquiries.",
    },
  ],

  ui: {
    title: "MRE Assistant",
    subtitle: "Ask about our services or get a free quote",
    greeting:
      "Hi — I'm the MRE assistant.\n\nAsk me about anything we build or any digital work we do, and I'll point you to the right place. Or I can get your project straight to the team.",
    quickReplies: ["What services do you offer?", "Get a free quote", "What areas do you cover?", "How much does it cost?"],
    placeholder: "Ask a question…",
    fallback:
      "I don't have a confident answer to that one — I'd rather say so than guess.\n\nYou can rephrase it, call us, or leave your details and a human will come back to you.",
  },
};

export default CHAT_KB;
